import { createFileRoute, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { useConsentRecords } from '@/hooks/consent/useConsentRecords';
import { useRevokeConsent } from '@/hooks/consent/useRevokeConsent';
import { useContact } from '@/hooks/contacts/useContact';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import type { ConsentRecord } from '@/types/paymentSchedule';

export const Route = createFileRoute('/_dashboard/dashboard/contacts/$contactId/consent-records')({
  component: ContactConsentRecordsPage,
});

function ContactConsentRecordsPage() {
  const router = useRouter();
  const { contactId } = Route.useParams();
  const contactQuery = useContact(contactId);
  const consentsQuery = useConsentRecords(contactId, { activeOnly: false });
  const revokeMutation = useRevokeConsent(contactId);
  const [pendingRevoke, setPendingRevoke] = useState<ConsentRecord | null>(null);

  const records = consentsQuery.data ?? [];
  const active = records.filter((r) => isActive(r));
  const revoked = records.filter((r) => !isActive(r));

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            router.navigate({
              to: '/dashboard/contacts/$contactId',
              params: { contactId },
            })
          }
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to contact
        </Button>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-foreground">Auto-charge consent records</h1>
        <p className="text-sm text-muted-foreground">
          {contactQuery.data
            ? (contactQuery.data.displayName ??
              `${contactQuery.data.firstName ?? ''} ${contactQuery.data.lastName ?? ''}`.trim())
            : 'Contact'}{' '}
          · Explicit authorization is required before any automated charge will fire.
        </p>
      </div>

      {consentsQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : records.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No consent records on file. Auto-charge rules will refuse to fire until consent is
          captured.
        </p>
      ) : (
        <div className="space-y-6">
          <Section title="Active" count={active.length}>
            {active.map((record) => (
              <ConsentRow
                key={record.id}
                record={record}
                onRevoke={() => setPendingRevoke(record)}
              />
            ))}
          </Section>
          {revoked.length > 0 && (
            <Section title="Expired / Revoked" count={revoked.length}>
              {revoked.map((record) => (
                <ConsentRow key={record.id} record={record} />
              ))}
            </Section>
          )}
        </div>
      )}

      <AlertDialog
        open={pendingRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRevoke(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke consent?</AlertDialogTitle>
            <AlertDialogDescription>
              Future auto-charges using this consent will be blocked. The record is preserved for
              audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingRevoke) {
                  revokeMutation.mutate(
                    { id: pendingRevoke.id },
                    { onSettled: () => setPendingRevoke(null) },
                  );
                }
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-2 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <Badge variant="outline">{count}</Badge>
      </header>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

function ConsentRow({ record, onRevoke }: { record: ConsentRecord; onRevoke?: () => void }) {
  const active = isActive(record);
  const companyTimezone = useCompanyTimezone();
  const max = Number(record.maxAmountPerCharge);
  return (
    <li className="rounded-md border border-border bg-card p-3 text-sm shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {active ? (
              <ShieldCheck className="h-4 w-4 text-green-600" />
            ) : (
              <ShieldOff className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium text-foreground">{sourceLabel(record.capturedVia)}</span>
            <Badge variant={active ? 'default' : 'outline'}>
              {active ? 'Active' : 'Revoked / Expired'}
            </Badge>
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <Detail label="Max per charge" value={formatMoney(max)} />
            <Detail label="Signed at" value={formatDateTime(record.signedAt, companyTimezone)} />
            <Detail
              label="Expires"
              value={
                record.expiresAt ? formatDateTime(record.expiresAt, companyTimezone) : 'No expiry'
              }
            />
            <Detail label="IP" value={record.ipAddress} />
            <Detail label="User agent" value={record.userAgent} />
            <Detail label="Method" value={record.paymentMethodId} />
          </dl>
        </div>
        {active && onRevoke && (
          <Button variant="outline" size="sm" onClick={onRevoke}>
            Revoke
          </Button>
        )}
      </div>
    </li>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

function sourceLabel(s: ConsentRecord['capturedVia']): string {
  if (s === 'ESTIMATE_ACCEPTANCE') return 'Captured at estimate acceptance';
  if (s === 'SAVE_CARD_ON_FILE') return 'Captured via Save Card';
  if (s === 'SETTINGS_PAGE') return 'Captured from settings';
  return s;
}

function isActive(record: ConsentRecord): boolean {
  if (!record.expiresAt) return true;
  return new Date(record.expiresAt).getTime() > Date.now();
}

function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
}

function formatDateTime(iso: string, timeZone?: string | null): string {
  return (
    formatDateTimeInTimeZone(iso, timeZone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) || iso
  );
}
